// @flow strict

import * as React from 'react';
import type {CustomFormField} from 'src/types/crm';
import classify from 'src/utils/classify';
import {Dropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import css from './index.css';

// $FlowFixMe[unclear-type] - Using any type to avoid complex component compatibility issues
type FormikFieldType = any;

type MultiSelectDropDownProps = {
  field: CustomFormField,
  formikField: FormikFieldType,
  onChange: (Array<string>) => void,
  error: ?string,
  touched: boolean,
  required: boolean,
  label: string,
  classNames?: string,
};

export const MultiSelectDropDown = ({
  formikField,
  field,
  onChange,
  error,
  touched,
  required,
  label,
  classNames,
}: MultiSelectDropDownProps): React.Node => {
  const clickAwayRef = React.useRef(null);
  const [dropDownText, setDropdownText] = React.useState('');
  const [selectedKeys, setSelectedKeys] = React.useState(
    formikField.value || [],
  );

  const menuOptions = React.useMemo(
    () =>
      field.customField.possibleValues.map((option) => ({
        key: option.possibleValue,
        label: option.possibleValue,
      })),
    [field.customField.possibleValues],
  );

  React.useEffect(() => {
    setSelectedKeys(formikField.value || []);
    const newDropdownText = getTextLabelFromSelectedKeys(
      formikField.value,
      menuOptions,
    );
    setDropdownText(newDropdownText);
  }, [formikField.value, menuOptions]);

  const getTextLabelFromSelectedKeys = (keys = [], options = []) => {
    if (!keys || !keys.length) {
      return '';
    }
    return keys
      .map((key) => options.find((opt) => opt.key === key)?.label)
      .filter(Boolean)
      .join(', ');
  };

  const getSelectedKeysFromSelectedOption = (option, currentKeys = []) => {
    const keyIndex = currentKeys.indexOf(option.key);
    if (keyIndex === -1) {
      return [...currentKeys, option.key];
    }
    return currentKeys.filter((key) => key !== option.key);
  };

  const handleChange = (option) => {
    console.log(option, 'OPTION 12345678');
    const newSelectedKeys = getSelectedKeysFromSelectedOption(
      option,
      selectedKeys,
    );

    const newDropdownText = getTextLabelFromSelectedKeys(
      newSelectedKeys,
      menuOptions,
    );
    setSelectedKeys(newSelectedKeys);
    setDropdownText(newDropdownText);
    onChange(newSelectedKeys);
  };

  return (
    <Dropdown
      {...formikField}
      label={label}
      placeholder={field.customField.placeholder}
      dropdownInputText={dropDownText}
      menu={{
        options: menuOptions,
        selectedKeys,
        optionsVariant: 'checkbox',
      }}
      onChange={handleChange}
      clickAwayRef={clickAwayRef}
      required={required}
      error={Boolean(error && touched)}
      errorText={error || ''}
      classNames={{wrapper: classify(css.dropdownInputContainer, classNames)}}
    />
  );
};

type SingleSelectDropDownProps = {
  field: CustomFormField,
  formikField: FormikFieldType,
  onChange: (string) => void,
  error: ?string,
  touched: boolean,
  required: boolean,
  label: string,
  classNames?: string,
};

export const SingleSelectDropDown = ({
  formikField,
  field,
  onChange,
  error,
  touched,
  label,
  required,
  classNames,
}: SingleSelectDropDownProps): React.Node => {
  const clickAwayRef = React.useRef(null);
  const [dropDownText, setDropdownText] = React.useState('');
  const [selectedKey, setSelectedKey] = React.useState(formikField.value || '');

  const menuOptions = React.useMemo(
    () =>
      field.customField.possibleValues.map((option) => ({
        key: option.possibleValue,
        label: option.possibleValue,
      })),
    [field.customField.possibleValues],
  );

  React.useEffect(() => {
    setSelectedKey(formikField.value || '');
    const selectedOption = menuOptions.find(
      (opt) => opt.key === formikField.value,
    );
    setDropdownText(selectedOption?.label || '');
  }, [formikField.value, menuOptions]);

  const handleChange = (option) => {
    setSelectedKey(option.key);
    setDropdownText(option.label);
    onChange(option.key);
  };

  return (
    <Dropdown
      {...formikField}
      label={label}
      placeholder={field.customField.placeholder}
      dropdownInputText={dropDownText}
      menu={{
        options: menuOptions,
        selectedKeys: selectedKey ? [selectedKey] : [],
      }}
      onChange={handleChange}
      clickAwayRef={clickAwayRef}
      required={required}
      error={Boolean(error && touched)}
      errorText={error || ''}
      classNames={{wrapper: classify(css.dropdownInputContainer, classNames)}}
    />
  );
};
